"use strict";

import Global from "/js/core/global.js";

/**
 * Analytics logic.
 */

export default class Analytics {

  static #CONTENT_ID;
  static #TYPE;
  static #PARTNER_ID;
  static #LANGUAGE_CODE;

  static #isInitialized;

  /* Whether the current layout is mobile or not. Updates when breakpoint changes in trackBreakpointChange method. */
  static #isMobileLayout;
  /* The source from where a lesson originated when it is opened. */
  static #lessonTrackingSource = "lesson";
  /* The source from where the dashboard button was clicked. */
  static #dashboardButtonTrackingSource = "lesson";
  /* Whether the current lesson has been completed before. Set before lesson completion is stored on the user. */
  static #isRepeatedLessonComplete;

  /**
  * Must be called on start-up.
   * @param contentId minicourse or lesson id
   * @param type possible values are "minicourse" and "lesson"
   * @param partnerId id of the partner
   * @param languageCode ISO 639-1 code for the current language.
   * @param isMobileLayout whether the device is mobile or not.
  */
  static init(contentId, type, partnerId, languageCode, isMobileLayout) {
    if (!Global.isProd) {
      return;
    }

    this.#CONTENT_ID = contentId;
    this.#TYPE = type;
    this.#PARTNER_ID = partnerId;
    this.#LANGUAGE_CODE = languageCode;
    this.#isMobileLayout = isMobileLayout;

    // Initialize Firebase
    const firebaseConfig = {
      apiKey: "AIzaSyDbBs0nCskl9KWdMEaCvc9kZMGrC9S97C0",
      authDomain: "primer-api.firebaseapp.com",
      databaseURL: "https://primer-api.firebaseio.com",
      projectId: "primer-api",
      storageBucket: "primer-api.appspot.com",
      messagingSenderId: "44497133497",
      appId: "1:44497133497:web:e9acc2b1d627b576ef2068",
      measurementId: "G-R086K15JYW"
    };
    try {
      firebase.initializeApp(firebaseConfig);
      firebase.analytics();
      firebase.analytics().setUserProperties({
        content_id: contentId,
        partner_id: partnerId,
        app_language: languageCode
      });
    } catch (e) {
      clw("analytics init failed: ", e);
      return;
    }

    this.#isInitialized = true;

    this.send("ComponentStart");
  }

  /**
   * Sends data to Google Analytics. This is the single function from which
   * this should occur.
   */
  static send(eventName, eventParams) {

    const defaultParams = {
      "componentId": this.#CONTENT_ID,
      "componentType": this.#TYPE,
      "partnerId": this.#PARTNER_ID,
      "language": this.#LANGUAGE_CODE,
      "isMobileLayout": this.#isMobileLayout ? "true" : "false"
    };
    const params = eventParams ? {...eventParams,...defaultParams} : defaultParams;

    if (!Global.isProd) {
      // Don't send, just print the info
      cl(`Analytics.send()`, eventName, params);
    }
    if (this.#isInitialized) {
      firebase.analytics().logEvent(eventName, params);
    }

    return eventName;
  }

  static set lessonTrackingSource(source) {
    this.#lessonTrackingSource = source;
  }

  static set dashboardButtonTrackingSource(source) {
    this.#dashboardButtonTrackingSource = source;
  }

  static get isRepeatedLessonComplete() {
    return this.#isRepeatedLessonComplete;
  }

  static set isRepeatedLessonComplete(isRepeated) {
    this.#isRepeatedLessonComplete = isRepeated;
  }

  /*** Tracking events: ***/

  static trackInteractionStart(interactions, interactionId) {
    let eventName = this.#getPrefixForInteraction(interactions, interactionId) + "StartIX";
    this.send(eventName, this.#getInteractionParams(interactionId));
  }

  static trackInteractionSkip(interactions, interactionId) {
    let eventName = this.#getPrefixForInteraction(interactions, interactionId) + "SkipIX";
    this.send(eventName, this.#getInteractionParams(interactionId));
  }

  static trackInteractionEnd(interactions, interactionId) {
    let eventName = this.#getPrefixForInteraction(interactions, interactionId) + "EndIX";
    this.send(eventName, this.#getInteractionParams(interactionId));
  }

  /* TBD
  static trackInteractionReplay(interactionId) {
    this.send("DoThisNowReplayIX", this.getInteractionParams(interactionId));
  }
  */

  static trackLessonSwipePrevious(cardIndex) {
    this.send("LessonSwipePrevious", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonSwipeNext(cardIndex) {
    this.send("LessonSwipeNext", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonButtonPrevious(cardIndex) {
    this.send("LessonButtonPrevious", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonButtonNext(cardIndex) {
    this.send("LessonButtonNext", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonTapNext(cardIndex) {
    this.send("LessonTapNext", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonWheelNext(cardIndex) {
    this.send("LessonWheelNext", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonWheelPrevious(cardIndex) {
    this.send("LessonWheelPrevious", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonArrowKeyNext(cardIndex) {
    this.send("LessonArrowKeyNext", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonArrowKeyPrevious(cardIndex) {
    this.send("LessonArrowKeyPrevious", this.#getCardNavigationParams(cardIndex));
  }

  static trackLessonTapToReveal() {
    this.send("LessonTapToReveal", { 
      "lessonId": Global.lessonId 
    });
  }

  static trackLessonListItemSelected(lessonId, progress) {
    // progress: not_started | in_progress | completed
    // source: lesson | lesson_set | sidebar
    this.send("LessonListItemSelected", {
      "lessonId": lessonId,
      "progress": progress,
      "source": this.#lessonTrackingSource
    });
  }

  static trackLessonView(isRepeated, totalLessons) {
    // source: lesson | lesson_set | sidebar | recap | recap-next-lesson
    this.send("LessonView", { 
      "lessonId": Global.lessonId,
      "isRepeated": isRepeated,
      "totalLessons": totalLessons,
      "source": this.#lessonTrackingSource,
      "setId": Global.minicourseData ? Global.minicourseData.id : ""
    });
  }

  static trackLessonComplete(totalLessons, nextLessonId) {
    // source: lesson | lesson_set | sidebar | recap | recap-next-lesson
    this.send("LessonComplete", { 
      "lessonId": Global.lessonId,
      "isRepeated": this.#isRepeatedLessonComplete ? "true" : "false",
      "totalLessons": totalLessons,
      "source": this.#lessonTrackingSource,
      "setId": Global.minicourseData ? Global.minicourseData.id : "",
      "type": this.#getLessonType(nextLessonId),
      "nextLessonId": nextLessonId ? nextLessonId : ""
    });
  }

  static trackLessonsCompleteOne() {
    this.send("LessonsCompleteOne");
  }

  static trackLessonsCompleteTwo() {
    this.send("LessonsCompleteTwo");
  }

  static trackLessonsCompleteThree() {
    this.send("LessonsCompleteThree");
  }

  static trackLessonsCompleteFour() {
    this.send("LessonsCompleteFour");
  }

  static trackLessonCompleteOpenRecap(totalLessons, nextLessonId) {
    this.send("LessonCompleteOpenRecap", { 
      "lessonId": Global.lessonId,
      "isRepeated": this.#isRepeatedLessonComplete ? "true" : "false",
      "totalLessons": totalLessons,
      "setId": Global.minicourseData ? Global.minicourseData.id : "",
      "type": this.#getLessonType(nextLessonId),
      "nextLessonId": nextLessonId ? nextLessonId : ""
    });
  }

  static trackLessonCompleteBackToMinicourse() {
    this.send("LessonCompleteBackToMinicourse");
  }

  static trackLessonStartFromComplete(totalLessons, nextLessonId) {
    this.send("LessonStartFromComplete", { 
      "lessonId": Global.lessonId,
      "isRepeated": this.#isRepeatedLessonComplete ? "true" : "false",
      "totalLessons": totalLessons,
      "setId": Global.minicourseData ? Global.minicourseData.id : "",
      "type": this.#getLessonType(nextLessonId),
      "nextLessonId": nextLessonId ? nextLessonId : ""
    });
  }

  // A new lesson (nextLessonId) was started from the Recap screen of the Global lesson.
  static trackLessonStartFromRecap(isRepeated, totalLessons, nextLessonId) {
    // type: standalone | lesson_of_set | set_complete
    this.send("LessonStartFromRecap", {
      "lessonId": Global.lessonId,
      "isRepeated": isRepeated ? "true" : "false",
      "totalLessons": totalLessons,
      "setId": Global.minicourseData ? Global.minicourseData.id : "",
      "type": this.#getLessonType(nextLessonId),
      "nextLessonId": nextLessonId ? nextLessonId : ""
    });
  }

  static trackLessonRecapView() {
    this.send("LessonRecapView", { 
      "lessonId": Global.lessonId 
    });
  }

  static trackLessonRecapReplay() {
    this.send("LessonRecapReplay", { 
      "lessonId": Global.lessonId 
    });
  }

  static trackSetStart(lessonId) {
    this.send("SetStart", { 
      "lessonId": lessonId,
      "setId": Global.minicourseData.id
    });
  }

  static trackSetComplete() {
    this.send("SetComplete", { 
      "lessonId": Global.lessonId,
      "setId": Global.minicourseData.id
    });
  }

  static trackSetView() {
    this.send("SetView", { 
      "setId": Global.minicourseData.id,
      "source": "partner"
    });
  }

  static trackPrimerButton(source) {
    // source: desktop_footer_list | desktop_footer_lesson | mobile_drawer | mobile_list | mobile_recap
    this.send("PrimerButton", {
      "source": source
    });
  }

  static trackBackToDashboardButton() { // source: lesson | lesson_complete | recap
    this.send("BackToDashboardButton", { 
      "lessonId": Global.lessonId,
      "source": this.#dashboardButtonTrackingSource
    });
  }

  static trackSideBarButtonOpen(method) { // method: click | drag
    this.send("SideBarButtonOpen", { 
      "method": method
    });
  }

  static trackSideBarButtonClose(method) { // method: click | drag | screen
    this.send("SideBarButtonClose", { 
      "method": method
    });
  }

  static trackBreakpointChange(isMobileLayout) { // type: desktop | mobile
    this.#isMobileLayout = isMobileLayout;
    this.send("BreakpointChange", { 
      "type": isMobileLayout ? "mobile" : "desktop"
    });
  }

  static trackCustomCTA(url) {
    this.send("CustomCTA", { 
      "url": url
    });
  }

  static trackStartupError(type, extra=null) {
    const o = {
      type: type
    };
    if (extra) {
      o.extra = extra;
    }
    clw("*****************");
    clw("StartupError");
    clw("type:", type);
    clw("extra:", extra);
    clw("*****************");
    this.send("StartupError", o);
  }

  /*** Helpers ***/

  static #getInteractionParams(interactionId) {
    return { 
      "lessonId": Global.lessonId,
      "interactionType": interactionId
    }
  }

  static #getCardNavigationParams(cardIndex) {
    return { 
      "lessonId": Global.lessonId, 
      "currentCardIndex": cardIndex
    }
  }

  static #getPrefixForInteraction(interactions, interactionId) {
    const interactionsCount = interactions.length;
    let interactionPrefix;
    let index = 0;
    for (const interaction of interactions) {
      if (interaction.interactionId === interactionId) {
        if (interaction.shouldSave) {
          interactionPrefix = "DoThisNow";
        } else {
          if (interactionsCount > 2 && index > 0) {
            interactionPrefix = "BodyInteraction";
          } else {
            interactionPrefix = "QuickStart";
          }
        }
      }
      index++;
    }
    return interactionPrefix;
  }

  static #getLessonType(nextLessonId = "") {
    if (!Global.minicourseData) {
      return "standalone";
    } else {
      return nextLessonId ? "lesson_of_set" : "set_complete";
    }
  }
}
